import React, { useMemo, useState } from 'react';
import { Menu, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { PiNotepadBold } from "react-icons/pi";

import { UserIcon, DoorExitIcon, KeyIcon, PdfIcon, SwapIcon } from '../../icons';

import ProfileMenuItem from './ProfileMenuItem';
import ChangePasswordDialog from '../../auth/ChangePasswordDialog';
import PdfSettingsDialog from '../../auth/PdfSettingsDialog';
import ChangeTenantDialog from '../../auth/ChangeTenantDialog';
import { useAuth } from '../../../contexts/AuthContext';
import { useReleaseNotesStore } from '../../../hooks/useReleaseNotesStore';
import { useNavigate } from 'react-router';
import SettingsApiKeyDialog from './SettingsApiKeyDialog';

export interface ProfileMenuProps {
	anchorEl: HTMLElement | null;
	onClose: () => void;
	onLogout: () => void;
}

const ProfileMenu: React.FC<ProfileMenuProps> = observer(({
	anchorEl,
	onClose,
	onLogout,
}) => {
	const navigate = useNavigate();
    const { user } = useAuth();
	const theme = useTheme();
	const { versionNotifications, clearVersionNotifications } = useReleaseNotesStore();

	const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
	const [isPdfSettingsOpen, setIsPdfSettingsOpen] = useState(false);
	const [isChangeTenantOpen, setIsChangeTenantOpen] = useState(false);
	const [isSettingsOpen, setIsSettingsOpen] = useState(false)

	const isOpen = Boolean(anchorEl);

	const tenants = useMemo(() => {
        return user?.backend?.available_tenants?.map(tenant=> 
			({value: tenant.id, label: tenant.name})
		) || [];
    }, [user]);

	const handleChangePassword = () => {
		onClose();
		setIsChangePasswordOpen(true);
	};

	const handleChangeTenant = () => {
		onClose();
		setIsChangeTenantOpen(true);
	};

	const handleSignature = () => {
		onClose();
		setIsPdfSettingsOpen(true);
	};

	const handleReleaseNotes = () => {
		onClose();
		if (versionNotifications) {
			clearVersionNotifications();
		}
		navigate("/release-notes");
	}

	const handleSettings = () => {
		onClose();
		setIsSettingsOpen(true)	
	}

	return (
		<>
			<Menu
				anchorEl={anchorEl}
				open={isOpen}
				onClose={onClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				sx={{
					'& .MuiPaper-root': {
						mt: 1,
						minWidth: 192,
						boxShadow: theme.shadows[8],
						border: `1px solid ${theme.palette.divider}`,
					},
				}}
			>
				<ProfileMenuItem
					icon={KeyIcon}
					label="Change Password"
					onClick={handleChangePassword}
				/>

				{	// Show change tenant option only if more than one tenant is available
					tenants.length > 1 ? 
						<ProfileMenuItem
							icon={SwapIcon}
							label="Change Company"
							onClick={handleChangeTenant}
						/> : null
				}

				<ProfileMenuItem
					icon={PdfIcon}
					label="Signature"
					onClick={handleSignature}
				/>

				<ProfileMenuItem
					icon={UserIcon}
					label="Edit Profile"
					disabled
				/>

				<ProfileMenuItem
					icon={PiNotepadBold}
					label="Release Notes"
					onClick={handleReleaseNotes}
					notificationTooltip={versionNotifications ? "New release note published" : undefined}
				/>

				{/* Keep disabled till required
				 <ProfileMenuItem
					icon={MdOutlineSettings}
					label="Settings"
					onClick={handleSettings}
				/> */}

				<ProfileMenuItem
					icon={DoorExitIcon}
					label="Logout"
					onClick={onLogout}
				/>
			</Menu>

			{/* Dialogs */}
			<ChangePasswordDialog 
				isOpen={isChangePasswordOpen} 
				onClose={() => setIsChangePasswordOpen(false)} />

			<ChangeTenantDialog 
				isOpen={isChangeTenantOpen} 
				tenantOptions={tenants}
				tenant={user?.backend?.current_tenant?.id}
				onClose={() => setIsChangeTenantOpen(false)} />

			<PdfSettingsDialog 
				isOpen={isPdfSettingsOpen} 
				onClose={() => setIsPdfSettingsOpen(false)} />

			<SettingsApiKeyDialog
				isOpen={isSettingsOpen}
				onClose={() => setIsSettingsOpen(false)} />
		</>
	);
});

export default ProfileMenu;

